import {observable, runInAction} from "mobx";
import {Plan} from "types";

export const GOOGLE_ID = process.env.REACT_APP_GOOGLE_ID
export const ENV_NAME = (process.env.REACT_APP_ENV || "app").toLowerCase()
export const IS_PROD = ENV_NAME === "app"
export const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID
export const BASE_URL = process.env.REACT_APP_BASE_URL
export const BACKEND_URL = process.env.REACT_APP_BACKEND_API_URL
export const MIXPANEL_TOKEN = process.env.REACT_APP_MIXPANEL_TOKEN

export const SECRET_KEY = process.env.REACT_APP_SECRET_KEY
// Admin secret for adminUtil console helpers. Only set in the local dev env,
// so on dev/prod builds it stays undefined and the localStorage flow is used.
export const ADMIN_SECRET = process.env.REACT_APP_ADMIN_SECRET
export const LOG_EVENTS = process.env.REACT_APP_LOG_EVENTS === "true"
export const LANDING_URL = process.env.REACT_APP_LANDING_URL
export const DEV_ACCESS_TOKEN = process.env.REACT_APP_DEV_ACCESS_TOKEN
export const EMAIL_CONTACT = process.env.REACT_APP_EMAIL_CONTACT
export const BOOK_DEMO_URL = process.env.REACT_APP_BOOK_DEMO_URL
// Cello referral programme; empty ⇒ the integration is off for this env.
export const CELLO_SCRIPT_URL = process.env.REACT_APP_CELLO_SCRIPT_URL || ""


// Min characters length
export const MIN_LENGTH = 200
// Below this many scannable words left, a "scan the first part" offer isn't worth it —
// the scan-limit paywall drops the free-continue action and shows a plain paywall.
export const MIN_CONTINUE_WORDS = 100

// Trial lifecycle: show the "trial ending soon" in-editor banner when the trial
// has this many days (or fewer) left. Drives TRIAL_ENDING_SOON (spec §9.2).
export const TRIAL_ENDING_DAYS = 3;

export enum FEATURES {
    PLAGIARISM = "PLAGIARISM",
    FREE_PREMIUM = "FREE_PREMIUM",
    GRAMMAR = "GRAMMAR",
    ONBOARDING = "ONBOARDING",
    HUMANIZER = "HUMANIZER",
    TRIAL = "TRIAL",
    ONE_TIME_SCAN = "ONE_TIME_SCAN",
    AB_URL_OVERRIDE = "AB_URL_OVERRIDE",
}

const enabled_features = localStorage.getItem("ENABLED") || ""
const disabled_features = localStorage.getItem("DISABLED") || ""

const featureOverrides = observable.map<FEATURES, boolean>();

export const setFeatureEnabled = (feature: FEATURES, enabled: boolean | null) => {
    runInAction(() => {
        if (enabled === null) {
            featureOverrides.delete(feature);
        } else {
            featureOverrides.set(feature, enabled);
        }
    });
}

// Hard-coded defaults per feature — explicit, with no env logic mixed in.
const FEATURE_DEFAULTS: Record<FEATURES, boolean> = {
    [FEATURES.PLAGIARISM]: true,
    [FEATURES.FREE_PREMIUM]: false,
    // Grammar is on by default; disable per-env via REACT_APP_FEATURE_GRAMMAR=false.
    [FEATURES.GRAMMAR]: true,
    [FEATURES.ONBOARDING]: false,
    // The Humanizer ships off by default; a stand enables it with
    // REACT_APP_FEATURE_HUMANIZER=true together with the backend's HUMANIZER_ENABLED.
    [FEATURES.HUMANIZER]: false,
    // The 7-day Premium trial offer. Off: the paywall sells the plan at its price; the
    // code stays for when it comes back.
    [FEATURES.TRIAL]: false,
    // The one-time scan, offered to the treatment arm of its experiment (utils/abTest).
    [FEATURES.ONE_TIME_SCAN]: true,
    // `?<experiment cookie>=<variant>` puts the visitor in that A/B arm (utils/abTest). Off on
    // prod so nobody can pick their own arm; stands enable it with REACT_APP_FEATURE_AB_URL_OVERRIDE.
    [FEATURES.AB_URL_OVERRIDE]: false,
}

// Build-time per-feature override: REACT_APP_FEATURE_<NAME>=true|false flips a flag's default on a
// given env (dev/prod/local/extension) without touching code — replacing the old `!IS_PROD` coupling.
// Value is case-insensitive (True == true); unset / unrecognized ⇒ the code default above is kept.
// Note: the dynamic key relies on CRA (react-scripts) inlining the whole process.env object; a future
// Vite/DefinePlugin change would only inline literal reads, silently falling back to the code default.
const featureEnvOverride = (feature: FEATURES): boolean | undefined => {
    const raw = process.env[`REACT_APP_FEATURE_${feature}`]?.trim().toLowerCase();
    if (raw === "true") return true;
    if (raw === "false") return false;
    return undefined;
}

// Apply env overrides once at load, so the hot isFeatureEnabled path stays an O(1) map read.
for (const feature of Object.values(FEATURES) as FEATURES[]) {
    const override = featureEnvOverride(feature);
    if (override !== undefined) FEATURE_DEFAULTS[feature] = override;
}

export const isFeatureEnabled = (feature: FEATURES) => {
    if (featureOverrides.has(feature)) return featureOverrides.get(feature)!;
    // @ts-ignore
    if (window[feature]) return true;
    if (disabled_features.includes(feature)) return false;
    if (enabled_features.includes(feature)) return true;
    return FEATURE_DEFAULTS[feature];
}

// Features the embedded widget (/widgets/*, iframed by aichecker & aidetector) ships
// without — ScanWidgetPage turns these off on mount and restores them on unmount. It
// can't come from the env: the widget is the same bundle served from the same origin as
// the app, so only the route can tell them apart.
export const WIDGET_DISABLED_FEATURES: FEATURES[] = [FEATURES.PLAGIARISM, FEATURES.GRAMMAR, FEATURES.HUMANIZER]

export const FILES_ACCEPT = {
    'application/pdf': ['.pdf'],
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": ['.docx'],
    'text/plain': ['.txt']
}


console.log("BASE_URL", BASE_URL)
console.log("BACKEND_URL", BACKEND_URL)
console.log("MIXPANEL_TOKEN", MIXPANEL_TOKEN)
console.log("LANDING_URL", LANDING_URL)

export const PLANS_ORDER: Plan[] = ["free", "plus", "premium", "pro", "enterprise"]

// Upgrade funnel ladder: the price order minus the legacy "plus" tier, which we
// never offer as an upgrade target (free *and* plus subscribers are pushed to
// Premium). getNextPlan walks this, so a free user is sent to Premium, not Plus;
// pickCoveringPlan (utils/planCoverage) walks the same order and stops at the
// first tier that actually covers the run being sold.
export const UPSELL_LADDER: Plan[] = PLANS_ORDER.filter(p => p !== "plus")

// Every plan we may upsell `current` to, cheapest first. The one place the funnel's
// two policy rules live: a plus subscriber upgrades like a free one, and a plan outside
// the ladder has no upsell at all.
// Off-ladder values do reach here: /users/me reports `subscription: "Pay as you go"` for
// any Free account carrying additional_limits (User.subscription_name), and other services'
// tiers (aichecker_premium, educational) can appear too. They get no offer — the same dead
// end getNextPlan / CHAR_LIMIT_UPSELL_TARGET gave them before, preserved deliberately rather
// than fixed here, because routing a pay-as-you-go buyer would also have to settle whether
// their purchase is a new subscription or a plan change (see UserStore.wordWallet).
// getNextPlan takes the first; pickCoveringPlan takes the first that covers the run.
export const upsellCandidates = (current: Plan | null): Plan[] => {
    if (!current) return []
    const from = current === "plus" ? "free" : current
    const index = UPSELL_LADDER.indexOf(from)
    return index === -1 ? [] : UPSELL_LADDER.slice(index + 1)
}

// Viewport widths (inclusive maxima), the same numbers as $bp-mobile / $bp-compact in
// _tokens.scss: up to MOBILE_MAX_WIDTH the mobile tree renders; up to COMPACT_MAX_WIDTH
// the sidebar starts collapsed and expands as an overlay.
export const MOBILE_MAX_WIDTH = 767;
export const COMPACT_MAX_WIDTH = 1279;
// Below the 1440 frame the narrower variants apply (e.g. the Humanizer's mode dropdown).
export const NARROW_DESKTOP_MAX_WIDTH = 1439;
// Up to here an account's header (referral launcher, trial pill, avatar) leaves the scan
// tabs no room for the language switcher: measured on the widest one (es, on a trial).
export const LANGUAGE_IN_SIDEBAR_MAX_WIDTH = 1119;
export const MOBILE_BREAKPOINT = `${MOBILE_MAX_WIDTH}px`;

// How long a transient notice (the editor's InfoBox, "Coming soon") stays up.
export const NOTICE_TTL_MS = 4000;
// A toast's two lines take longer to read than a notice's one.
export const TOAST_TTL_MS = 6000;

export const HUMANIZER_MODES = ['easy', 'medium', 'aggressive'] as const;
export type HumanizerMode = typeof HUMANIZER_MODES[number];

// Every hover-raised tooltip waits this long before showing (per design).
export const TOOLTIP_HOVER_DELAY_MS = 500;
// The Humanizer's transient tag tooltips (first-result hint, "text changed")
// dismiss themselves after this, unless the tag is hovered first.
export const HUMANIZER_TIP_TTL_MS = 6000;
export const POLICY_URL = "https://its-ai.org/policy.html";

/**
 * Every language the app ships, in the order the switcher lists them.
 *
 * This is the single place a language is added. `code` is the `/<lang>/` route
 * prefix and the .ftl filename; `label` is how the switcher names it (in that
 * language — that's what someone scanning the list looks for); `rtl` drives the
 * document direction; `detectorName` is what the `languagedetect` package calls
 * it, used to decide whether pasted text is scannable.
 *
 * Keep `code` in step with the backend's UI_LOCALES: the backend builds the
 * Stripe return URL from its own list, and a locale it doesn't know sends the
 * buyer back into English.
 */
export const LANGUAGES = [
    {code: 'en', label: 'English', rtl: false, detectorName: 'english'},
    {code: 'ar', label: 'العربية', rtl: true, detectorName: 'arabic'},
    {code: 'fr', label: 'Français', rtl: false, detectorName: 'french'},
    {code: 'de', label: 'Deutsch', rtl: false, detectorName: 'german'},
    {code: 'es', label: 'Español', rtl: false, detectorName: 'spanish'},
] as const;

export const DEFAULT_LANGUAGE_CODE = 'en';

/** Languages the detector accepts as scan input, named as `languagedetect` reports them. */
export const supportedLanguages: string[] = LANGUAGES.map((l) => l.detectorName);

/**
 * Locales @it-s-ai/auth ships copy for — its login, onboarding and
 * inline-checkout screens. The package gained fr/de/es in 3.1.0, which is what
 * package.json now requires, so this is every language we ship.
 *
 * It stays a separate list because the package versions independently: if it
 * ever lags behind a new locale here, narrow this rather than let its screens
 * fall through to raw message keys.
 */
export const AUTH_PACKAGE_LOCALES: string[] = LANGUAGES.map((l) => l.code);

/** Languages enabled for the product in the Cello portal (no Arabic there);
 *  the referral widget boots in the default language for the rest. */
export const CELLO_LOCALES: string[] = ['en', 'fr', 'de', 'es'];
